// Mock data for Buzz Craft Maven website - A Growth Collective

export const services = [
  // Digital Marketing Vertical
  {
    id: 1,
    title: "SEO & Content Marketing",
    slug: "seo-content-marketing",
    description: "We help your brand get discovered. Through smart keyword strategies and compelling content, we make sure the right people find you at the right time.",
    fullDescription: `In today's digital world, being invisible online is like having a shop with no signboard. SEO & Content Marketing is how we make sure your business gets found by the people who are actively searching for what you offer. We don't just stuff keywords—we create a strategic approach that brings the right visitors to your website.

Our team researches what your potential customers are searching for, then creates valuable content that answers their questions and solves their problems. This builds trust with your audience while signaling to Google that your site deserves to rank higher. It's a win-win approach that delivers long-term results.

Whether you're a local business wanting to dominate your area or an e-commerce brand looking to compete nationally, we tailor our SEO strategy to your specific goals. From technical fixes to content creation, we handle everything so you can focus on running your business.`,
    icon: "Search",
    level: "Digital Marketing",
    xp: 850,
    vertical: "digital-marketing",
    features: ["Keyword Strategy", "Content Creation", "Technical SEO", "Link Building"],
    benefits: [
      "Get found by customers actively searching for your services",
      "Build long-term organic traffic without ongoing ad spend",
      "Establish your brand as an authority in your industry",
      "Generate quality leads that convert into paying customers"
    ],
    cta: { text: "Get Free SEO Audit", link: "/audit" }
  },
  {
    id: 2,
    title: "Social Media Strategy",
    slug: "social-media-strategy",
    description: "Building communities, not just followers. We craft engaging content that sparks conversations and turns your audience into loyal fans.",
    fullDescription: `Social media isn't just about posting pretty pictures—it's about building real connections with real people. A strong social media strategy helps you show up consistently, share your story authentically, and create a community of people who genuinely care about your brand.

We start by understanding your audience: where they hang out online, what content they engage with, and what problems they're trying to solve. Then we create a content strategy that speaks directly to them. From eye-catching visuals to compelling captions, every post is designed to stop the scroll and start a conversation.

But we don't stop at posting. We monitor engagement, respond to comments, analyze what's working, and continuously optimize your strategy. The result? A growing community of followers who become customers, and customers who become brand advocates.`,
    icon: "Share2",
    level: "Digital Marketing",
    xp: 920,
    vertical: "digital-marketing",
    features: ["Content Calendar", "Community Management", "Analytics", "Influencer Partnerships"],
    benefits: [
      "Build a loyal community that engages with your brand",
      "Stay top-of-mind with consistent, quality content",
      "Turn followers into customers through strategic funnels",
      "Get real insights into what your audience wants"
    ],
    cta: { text: "Start Growing Your Audience", link: "/contact" }
  },
  {
    id: 3,
    title: "Paid Advertising",
    slug: "paid-advertising",
    description: "Every rupee counts. We create targeted campaigns on Google and Meta that bring real leads and measurable returns.",
    fullDescription: `When you need results fast, paid advertising is the accelerator. Unlike organic methods that take time to build, paid ads can put your business in front of thousands of potential customers within hours. But here's the catch—without the right strategy, you can burn through your budget with nothing to show for it.

That's where we come in. We don't just set up ads and hope for the best. We research your market, understand your ideal customer, and create campaigns that speak directly to their needs. Every ad, every keyword, every audience segment is chosen with purpose. And we track everything, so you know exactly where your money is going and what it's bringing back.

Whether it's Google Ads that capture people actively searching for your services, or Meta Ads that build awareness and drive traffic, we manage your campaigns with the same care we'd give our own money. Our goal is simple: maximum returns on your advertising investment.`,
    icon: "TrendingUp",
    level: "Digital Marketing",
    xp: 780,
    vertical: "digital-marketing",
    features: ["Google Ads", "Meta Ads", "Retargeting", "Conversion Optimization"],
    benefits: [
      "Get immediate visibility and traffic to your business",
      "Reach your exact target audience with precision targeting",
      "Track every rupee spent and measure real ROI",
      "Scale your campaigns as your business grows"
    ],
    cta: { text: "Launch Your Campaign", link: "/contact" }
  },
  {
    id: 4,
    title: "Brand Strategy",
    slug: "brand-strategy",
    description: "Your brand deserves a voice that resonates. We help you define who you are and craft messaging that cuts through the noise.",
    fullDescription: `Your brand is more than a logo—it's the feeling people get when they interact with your business. It's what makes someone choose you over a competitor, even when the products are similar. A strong brand strategy gives you clarity on who you are, who you serve, and why you matter.

We help you dig deep to uncover your brand's unique value proposition. What makes you different? What do you stand for? What story are you telling? These aren't just philosophical questions—they're the foundation of every marketing decision you'll make. Get them right, and everything else becomes easier.

From your visual identity to your tone of voice, from your tagline to your customer experience, we help you create a cohesive brand that resonates with your ideal customers. The result is a brand that people recognize, remember, and recommend to others.`,
    icon: "Sparkles",
    level: "Digital Marketing",
    xp: 890,
    vertical: "digital-marketing",
    features: ["Brand Positioning", "Voice & Tone", "Competitive Analysis", "Brand Guidelines"],
    benefits: [
      "Stand out from competitors with a unique brand identity",
      "Attract your ideal customers who connect with your values",
      "Create consistency across all your marketing channels",
      "Build trust and loyalty that drives repeat business"
    ],
    cta: { text: "Define Your Brand", link: "/contact" }
  },
  // Interior Design Vertical
  {
    id: 5,
    title: "Commercial Interior Design",
    slug: "commercial-interior-design",
    description: "Spaces that inspire productivity and impress clients. From offices to retail stores, we design environments where business thrives.",
    fullDescription: `Your workspace says a lot about your business before you even say a word. A well-designed commercial space doesn't just look good—it boosts productivity, impresses clients, and creates an environment where your team actually wants to work. That's the power of thoughtful interior design.

We work with offices, retail stores, restaurants, and hospitality spaces to create environments that serve your business goals. Need an office that encourages collaboration? A retail space that guides customers toward purchase? A restaurant that keeps people coming back? We design with both aesthetics and function in mind.

Our process starts with understanding your business, your brand, and your people. We then create designs that reflect your identity while optimizing the space for how it will actually be used. From concept to completion, we manage every detail so you get a space that works as hard as you do.`,
    icon: "Home",
    level: "Interior Design",
    xp: 910,
    vertical: "interior-design",
    features: ["Office Spaces", "Retail Design", "Restaurant Interiors", "Hospitality Design"],
    benefits: [
      "Create a professional environment that impresses clients",
      "Boost employee productivity and satisfaction",
      "Maximize your space for better functionality",
      "Reflect your brand identity in every corner"
    ],
    cta: { text: "Transform Your Space", link: "/contact" }
  },
  {
    id: 6,
    title: "Residential Interior Design",
    slug: "residential-interior-design",
    description: "Your home should tell your story. We create living spaces that feel uniquely yours, blending comfort with stunning aesthetics.",
    fullDescription: `Your home is your sanctuary—the place where you unwind, create memories, and truly be yourself. It should reflect who you are, not just follow the latest trends. That's the philosophy behind our residential interior design service: creating spaces that feel authentically yours.

We begin by getting to know you—your lifestyle, your preferences, your dreams for the space. Do you love hosting dinner parties? Need a peaceful reading nook? Want a kitchen that makes cooking a joy? Every design decision we make is guided by how you actually live, not just how things look in magazines.

From complete home makeovers to single-room refreshes, we handle everything from space planning to furniture selection to those final decorative touches. The result is a home that's beautiful, functional, and unmistakably yours—a place that welcomes you every time you walk through the door.`,
    icon: "Home",
    level: "Interior Design",
    xp: 880,
    vertical: "interior-design",
    features: ["Home Makeovers", "Space Planning", "Custom Furniture", "Décor Styling"],
    benefits: [
      "Live in a space that truly reflects your personality",
      "Maximize comfort and functionality in every room",
      "Get expert guidance on colors, materials, and layouts",
      "Save time and avoid costly design mistakes"
    ],
    cta: { text: "Design Your Dream Home", link: "/contact" }
  },
  // Photography Vertical
  {
    id: 7,
    title: "Brand & Product Photography",
    slug: "brand-product-photography",
    description: "A picture is worth a thousand sales. We capture your products and brand story in images that stop the scroll and start conversations.",
    fullDescription: `In a world of endless scrolling, your images have about two seconds to make an impression. Great product photography doesn't just show what you sell—it tells a story, evokes emotion, and makes people want what you're offering. That's the difference between a photo and a sales tool.

We specialize in capturing products and brands in their best light—literally. Whether you need clean e-commerce shots on white backgrounds, lifestyle images that show your products in action, or creative brand photography that tells your story, we have the skills and equipment to deliver stunning results.

Our process is collaborative and efficient. We discuss your vision, plan the shoot, handle all the creative direction, and deliver polished images ready for your website, social media, or marketing materials. The result? Photography that elevates your brand and drives real business results.`,
    icon: "Camera",
    level: "Photography",
    xp: 860,
    vertical: "photography",
    features: ["Product Photography", "Brand Shoots", "Lifestyle Imagery", "E-commerce Photos"],
    benefits: [
      "Showcase your products in the most appealing way",
      "Build trust with professional, high-quality imagery",
      "Stand out from competitors with unique visual content",
      "Increase conversions with scroll-stopping photos"
    ],
    cta: { text: "Book Your Shoot", link: "/contact" }
  },
  {
    id: 8,
    title: "Portrait & Corporate Photography",
    slug: "portrait-corporate-photography",
    description: "First impressions matter. Professional headshots and team photos that build trust and showcase the humans behind your brand.",
    fullDescription: `People do business with people, not logos. That's why professional portraits and corporate photography are so important—they put a human face on your brand and help potential clients feel connected before they ever meet you. A great headshot can open doors that a generic stock photo never will.

We create portraits that capture personality while maintaining professionalism. Whether you need individual headshots for your LinkedIn profile, team photos for your website, or coverage of corporate events, we ensure everyone looks their best while staying true to who they are.

Our approach is relaxed and efficient. We know most people aren't comfortable in front of a camera, so we guide you through poses and expressions that feel natural. The result is professional imagery that builds trust, establishes credibility, and shows the real people behind your business.`,
    icon: "Camera",
    level: "Photography",
    xp: 840,
    vertical: "photography",
    features: ["Professional Headshots", "Team Photos", "Corporate Events", "Executive Portraits"],
    benefits: [
      "Build instant trust with professional imagery",
      "Show the real people behind your business",
      "Upgrade your LinkedIn and website presence",
      "Create consistency across your team's photos"
    ],
    cta: { text: "Schedule Your Session", link: "/contact" }
  },
  // Web & App Development
  {
    id: 9,
    title: "Website Design & Development",
    slug: "website-design-development",
    description: "Your website is your 24/7 salesperson. We build fast, beautiful sites that turn curious visitors into paying customers.",
    fullDescription: `Your website is often the first interaction someone has with your business—and you never get a second chance at a first impression. A great website doesn't just look good; it loads fast, works perfectly on mobile, and guides visitors toward taking action. That's what we build.

We combine stunning design with technical excellence. Every website we create is custom-designed to reflect your brand, optimized for search engines, and built to convert visitors into leads or customers. We don't use cookie-cutter templates—your business is unique, and your website should be too.

From simple landing pages to complex e-commerce stores, we handle everything: design, development, content management systems, hosting setup, and ongoing support. We also make sure you can easily update your own content, so you're never dependent on us for simple changes. The result is a website that works for your business 24/7.`,
    icon: "Monitor",
    level: "Tech & Development",
    xp: 950,
    vertical: "tech",
    features: ["UI/UX Design", "Responsive Development", "Speed Optimization", "CMS Integration"],
    benefits: [
      "Make a powerful first impression on every visitor",
      "Convert more visitors into leads and customers",
      "Rank higher on Google with optimized performance",
      "Manage your content easily without technical skills"
    ],
    cta: { text: "Start Your Website Project", link: "/contact" }
  },
  {
    id: 10,
    title: "App Development",
    slug: "app-development",
    description: "Ideas deserve to come alive. We build mobile apps that users love to open, engage with, and recommend to friends.",
    fullDescription: `A great app can transform your business—putting you directly in your customers' pockets and creating engagement that websites simply can't match. But building an app that people actually use (and love) requires more than just coding skills. It requires understanding users, designing intuitive experiences, and executing flawlessly.

We build mobile apps for iOS and Android that solve real problems and deliver real value. Whether you need a customer-facing app to enhance your service, an internal tool to improve operations, or a startup app that's the foundation of your business, we have the expertise to bring your vision to life.

Our development process is collaborative and transparent. We start with understanding your goals and users, then move through design, development, testing, and launch with regular check-ins along the way. You'll always know where your project stands and have input at every stage.`,
    icon: "Smartphone",
    level: "Tech & Development",
    xp: 970,
    vertical: "tech",
    features: ["iOS Development", "Android Development", "Cross-Platform", "UI/UX Design"],
    benefits: [
      "Put your business directly in your customers' hands",
      "Create deeper engagement than websites alone",
      "Streamline operations with custom internal tools",
      "Launch on both iOS and Android efficiently"
    ],
    cta: { text: "Discuss Your App Idea", link: "/contact" }
  },
  // Graphic Design
  {
    id: 11,
    title: "Graphic Design",
    slug: "graphic-design",
    description: "Visuals that speak louder than words. From logos to social graphics, we create designs that capture attention and communicate your essence.",
    fullDescription: `Great design is invisible—it just works. When your logo, marketing materials, and social graphics all feel cohesive and professional, people trust your business more. They might not consciously notice the design, but they feel the difference. That's the power of thoughtful graphic design.

We create visual assets that help your brand shine across every touchpoint. Need a logo that captures your essence? Marketing brochures that close deals? Social media graphics that stop the scroll? Packaging that stands out on shelves? We design it all with consistency and purpose.

Our design process starts with understanding your brand and goals. We then create concepts, refine based on your feedback, and deliver polished files ready for use anywhere. Whether you're starting from scratch or refreshing an existing brand, we help you look as professional as you deserve.`,
    icon: "Palette",
    level: "Creative Services",
    xp: 880,
    vertical: "creative",
    features: ["Logo Design", "Marketing Collaterals", "Social Media Graphics", "Brand Identity"],
    benefits: [
      "Look professional across all your marketing materials",
      "Build brand recognition with consistent visuals",
      "Save time with ready-to-use design assets",
      "Stand out from competitors with unique designs"
    ],
    cta: { text: "Get Custom Designs", link: "/contact" }
  }
];

// Verticals for the collective
export const verticals = [
  {
    id: 1,
    name: "Digital Marketing",
    slug: "digital-marketing",
    icon: "TrendingUp",
    description: "SEO, Social Media, Paid Ads & Brand Strategy",
    color: "orange"
  },
  {
    id: 2,
    name: "Interior Design",
    slug: "interior-design",
    icon: "Home",
    description: "Commercial & Residential Spaces",
    color: "blue"
  },
  {
    id: 3,
    name: "Photography",
    slug: "photography",
    icon: "Camera",
    description: "Brand, Product & Corporate Photography",
    color: "purple"
  },
  {
    id: 4,
    name: "Tech & Development",
    slug: "tech",
    icon: "Monitor",
    description: "Websites & Mobile Apps",
    color: "cyan"
  },
  {
    id: 5,
    name: "Creative Services",
    slug: "creative",
    icon: "Palette",
    description: "Graphic Design & Branding",
    color: "pink"
  }
];

// Coming Soon Verticals
export const comingSoonVerticals = [
  {
    id: 1,
    name: "Podcast Production",
    icon: "Mic",
    description: "Full podcast production, editing & distribution",
    status: "Coming Soon"
  },
  {
    id: 2,
    name: "Funding & Investment",
    icon: "Wallet",
    description: "Startup funding assistance & investor connections",
    status: "Coming Soon"
  },
  {
    id: 3,
    name: "Video Production",
    icon: "Video",
    description: "Commercial videos, reels & brand films",
    status: "Coming Soon"
  }
];

export const auditServices = [
  {
    id: 1,
    title: "Social Media Audit",
    description: "Wondering why your posts aren't getting traction? We dig deep into your social presence to find hidden opportunities and quick wins.",
    icon: "Users",
    badge: "Popular",
    color: "orange",
    checkpoints: [
      "Profile completeness analysis",
      "Content performance review",
      "Engagement rate evaluation",
      "Competitor benchmarking",
      "Growth opportunity mapping",
      "Actionable recommendations"
    ]
  },
  {
    id: 2,
    title: "Website SEO Audit",
    description: "Not showing up on Google? We'll find out why and give you a clear roadmap to climb the rankings.",
    icon: "Globe",
    badge: "Essential",
    color: "black",
    checkpoints: [
      "On-page SEO health check",
      "Technical SEO analysis",
      "Page speed assessment",
      "Mobile friendliness review",
      "Content structure evaluation",
      "Priority fix recommendations"
    ]
  }
];

export const heroContent = {
  headline: "Where Great Ideas Come to Life",
  subtext: "We're a collective of passionate specialists in marketing, design, photography, and technology. Together, we help ambitious brands and dreamers turn their vision into reality.",
  stats: [
    { value: "150+", label: "Happy Clients" },
    { value: "5", label: "Verticals" },
    { value: "∞", label: "Possibilities" }
  ]
};

export const growthLevels = [
  {
    level: 1,
    title: "Listen",
    description: "We start by understanding your dreams and challenges.",
    icon: "Compass"
  },
  {
    level: 2,
    title: "Plan",
    description: "Together, we craft a roadmap tailored to your goals.",
    icon: "Map"
  },
  {
    level: 3,
    title: "Create",
    description: "Our specialists bring the plan to life with care.",
    icon: "Zap"
  },
  {
    level: 4,
    title: "Grow",
    description: "Watch your brand flourish and your metrics climb.",
    icon: "TrendingUp"
  },
  {
    level: 5,
    title: "Celebrate",
    description: "Success stories are made. Let's write yours.",
    icon: "Rocket"
  }
];

export const aboutContent = {
  headline: "We're Not an Agency. We're Your Growth Partners.",
  intro: "Buzz Craft Maven brings together talented specialists who genuinely care about your success. Whether you're launching a startup, redesigning your home, or capturing your brand's story, we're here to help you shine.",
  mission: {
    title: "Our Mission",
    content: "We believe every brand has a unique story worth telling. Our mission is to connect you with the right specialists at the right moment, so you can focus on what you do best while we handle the rest."
  },
  values: [
    {
      title: "Specialist-First",
      description: "Each project is handled by experts who truly love their craft. No generalists, just passionate specialists.",
      icon: "Target",
      xp: 100
    },
    {
      title: "Human-Centered",
      description: "Behind every project is a real person with real dreams. We listen, we care, and we deliver.",
      icon: "Users",
      xp: 100
    },
    {
      title: "Results That Matter",
      description: "Beautiful work is great, but results are better. We measure success by the impact we create for you.",
      icon: "Trophy",
      xp: 100
    }
  ]
};

export const contactContent = {
  headline: "Let's Create Something Beautiful",
  subtext: "Have a project in mind? A question to ask? Or just want to say hello? We'd love to hear from you.",
  email: "buzzcraftmaven@gmail.com",
  cta: "Start a Conversation"
};

export const socialLinks = {
  instagram: "https://www.instagram.com/buzzcraftmaven/",
  linkedin: "https://www.linkedin.com/company/buzz-craft-maven",
  email: "mailto:buzzcraftmaven@gmail.com"
};

export const officeLocations = {
  main: {
    city: "Chennai",
    address: "AVM Avenue, 8th Street",
    type: "Main Branch"
  },
  branches: [
    { city: "Hyderabad", type: "Branch Office" },
    { city: "Dubai, UAE", type: "Branch Office" }
  ]
};

export const navigation = [
  { name: "Home", path: "/" },
  { name: "About", path: "/about" },
  { name: "Services", path: "/services" },
  { name: "Blog", path: "/blog" },
  { name: "Audit", path: "/audit" },
  { name: "Contact", path: "/contact" }
];
